import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CalendarDays, Trash2, ChevronDown, ChevronUp, DollarSign, ShoppingBag, BookmarkPlus, Star, GripVertical, ArrowLeftRight } from 'lucide-react';
import { useAppStore } from '@/hooks/useAppStore';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { ROUTE_PATHS, SavedTrip, Meal } from '@/lib/index';
import { springPresets, staggerContainer, staggerItem } from '@/lib/motion';

// Days of the week for calendar grid
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

// ── Rating helpers (localStorage) ────────────────────────────────────────────
const RATINGS_KEY = 'flyerfeast-ratings';

function getAllRatings(): Record<string, number> {
  try { return JSON.parse(localStorage.getItem(RATINGS_KEY) ?? '{}'); } catch { return {}; }
}
export function getMealRating(id: string): number {
  return getAllRatings()[id] ?? 0;
}
export function setMealRatingLocal(id: string, stars: number): void {
  const all = getAllRatings();
  if (stars === 0) { delete all[id]; } else { all[id] = stars; }
  localStorage.setItem(RATINGS_KEY, JSON.stringify(all));
}

// ── Tiny inline star display (read-only, for calendar cells) ─────────────────
function StarsDisplay({ rating, size = 'xs' }: { rating: number; size?: 'xs' | 'sm' }) {
  if (!rating) return null;
  const sz = size === 'xs' ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5';
  return (
    <div className="flex items-center gap-0.5 mt-0.5 justify-center">
      {[1,2,3,4,5].map(s => (
        <Star key={s} className={`${sz} ${s <= rating ? 'fill-yellow-400 text-yellow-400' : 'fill-none text-muted-foreground/30'}`} />
      ))}
    </div>
  );
}

// ── Archived trip card ───────────────────────────────────────────────────────
function WeekCalendar({ trip, onDelete, currentMeals }: {
  trip: SavedTrip;
  onDelete: () => void;
  currentMeals: Meal[];
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [shareMsg, setShareMsg] = useState(false);
  const ratings = getAllRatings();

  const totalItems = trip.sections.reduce((s, sec) => s + sec.items.length, 0);

  const shareTrip = async () => {
    const lines = [
      `🔥 FlyersFeast — ${trip.name}`,
      `${trip.store} · ${new Date(trip.savedAt).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}`,
      `$${trip.totalCost.toFixed(2)} for the week`,
      '', '📅 Meals:',
      ...trip.meals.map((m, i) => `  ${DAYS[i] ?? `Day ${i+1}`}: ${m}`),
      '', '🛒 Grocery List:',
      ...trip.sections.flatMap(s => [
        s.section,
        ...s.items.map(i => `  ☐ ${i.name}  ${i.qty}${i.price > 0 ? ` — $${i.price.toFixed(2)}` : ''}`),
        '',
      ]),
    ];
    const text = lines.join('\n');
    if (navigator.share) {
      try { await navigator.share({ title: trip.name, text }); return; } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
    setShareMsg(true);
    setTimeout(() => setShareMsg(false), 2000);
  };

  // Try to find a meal in the current plan by name to get its ID for navigation
  const findMealId = (name: string): string | null => {
    const match = currentMeals.find(m => m.name === name);
    return match?.id ?? null;
  };

  return (
    <motion.div layout variants={staggerItem} className="bg-card border border-border rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h3 className="font-bold text-foreground">{trip.name}</h3>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><ShoppingBag className="w-3 h-3" />{trip.store}</span>
              <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{new Date(trip.savedAt).toLocaleDateString('en-CA',{month:'short',day:'numeric',year:'numeric'})}</span>
              <span className="flex items-center gap-1 text-primary font-semibold"><DollarSign className="w-3 h-3" />${trip.totalCost.toFixed(2)} total</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={shareTrip}
              className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1.5 rounded-full hover:bg-primary/20 transition-colors">
              {shareMsg ? '✓ Copied' : 'Share'}
            </button>
            <button onClick={onDelete} className="p-1.5 text-muted-foreground hover:text-red-500 transition-colors rounded-full">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 7-day calendar strip */}
        <div className="grid grid-cols-7 gap-1">
          {DAYS.map((day, i) => {
            const mealName = trip.meals[i];
            const mealId   = mealName ? findMealId(mealName) : null;
            const rating   = mealId ? (ratings[mealId] ?? 0) : 0;
            const isClickable = !!mealId;

            return (
              <button
                key={day}
                disabled={!mealName}
                onClick={() => mealId && navigate(`/recipe/${mealId}`)}
                className={`rounded-xl p-2 text-center transition-all ${
                  mealName
                    ? isClickable
                      ? 'bg-primary/10 border border-primary/20 hover:bg-primary/20 active:scale-95 cursor-pointer'
                      : 'bg-primary/10 border border-primary/20'
                    : 'bg-muted/40'
                }`}
              >
                <div className="text-[9px] font-bold text-muted-foreground uppercase mb-1">{day}</div>
                <div className="text-[10px] text-foreground font-medium leading-tight line-clamp-2 min-h-[2.5rem] flex flex-col items-center justify-center gap-0.5">
                  {mealName
                    ? <>
                        <span>{mealName}</span>
                        {rating > 0 && <StarsDisplay rating={rating} />}
                      </>
                    : <span className="text-muted-foreground/40">—</span>
                  }
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Expandable grocery list */}
      <button onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-xs text-muted-foreground hover:text-foreground transition-colors">
        <span>{totalItems} grocery items</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={springPresets.gentle}
            className="border-t border-border/50"
          >
            {trip.sections.map(sec => (
              <div key={sec.section}>
                <div className="px-4 py-2 bg-muted/20 text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  {sec.section}
                </div>
                {sec.items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-border/20 last:border-0">
                    <span className="text-sm text-foreground truncate flex-1 mr-3">{item.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {item.qty} {item.price > 0 ? `· $${item.price.toFixed(2)}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Current week interactive grid ────────────────────────────────────────────
function CurrentWeekGrid({ cartMeals, swapCartMeals, totalCost }: {
  cartMeals: Meal[];
  swapCartMeals: (i: number, j: number) => void;
  totalCost: number;
}) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<number | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  const ratings = getAllRatings();

  const handleDayClick = (idx: number) => {
    const meal = cartMeals[idx];
    // If we're in swap mode (something selected)
    if (selected !== null) {
      if (selected === idx) {
        // Deselect
        setSelected(null);
      } else {
        // Swap meals between days
        swapCartMeals(selected, idx);
        setSelected(null);
      }
      return;
    }
    // Normal click — navigate to recipe or select for swapping
    if (meal) {
      navigate(`/recipe/${meal.id}`);
    }
    // Clicking empty day while nothing selected — do nothing
  };

  const handleLongPress = (idx: number) => {
    if (cartMeals[idx]) {
      setSelected(idx);
    }
  };

  // Drag handlers (desktop)
  const handleDragStart = (idx: number) => setDragFrom(idx);
  const handleDragOver  = (e: React.DragEvent, idx: number) => { e.preventDefault(); setDragOver(idx); };
  const handleDrop      = (idx: number) => {
    if (dragFrom !== null && dragFrom !== idx) swapCartMeals(dragFrom, idx);
    setDragFrom(null); setDragOver(null);
  };
  const handleDragEnd   = () => { setDragFrom(null); setDragOver(null); };

  return (
    <div>
      {selected !== null && (
        <motion.div
          initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 mb-2.5 px-1"
        >
          <ArrowLeftRight className="w-3.5 h-3.5 text-primary shrink-0" />
          <p className="text-xs text-primary font-semibold">
            "{cartMeals[selected]?.name}" selected — tap another day to move it there
          </p>
          <button onClick={() => setSelected(null)} className="ml-auto text-xs text-muted-foreground hover:text-foreground">Cancel</button>
        </motion.div>
      )}
      {selected === null && cartMeals.length > 0 && (
        <p className="text-[10px] text-muted-foreground mb-2 px-1 flex items-center gap-1">
          <GripVertical className="w-3 h-3" /> Tap to open recipe · Long-press to move between days
        </p>
      )}
      <div className="grid grid-cols-7 gap-1">
        {DAYS.map((day, i) => {
          const meal   = cartMeals[i];
          const rating = meal ? (ratings[meal.id] ?? 0) : 0;
          const isSelected = selected === i;
          const isDragOver = dragOver === i;

          return (
            <div
              key={day}
              draggable={!!meal}
              onDragStart={() => handleDragStart(i)}
              onDragOver={e => handleDragOver(e, i)}
              onDrop={() => handleDrop(i)}
              onDragEnd={handleDragEnd}
              onClick={() => handleDayClick(i)}
              onContextMenu={e => { e.preventDefault(); handleLongPress(i); }}
              // Touch long press
              onTouchStart={() => {
                const timer = setTimeout(() => handleLongPress(i), 500);
                (window as any).__lpTimer = timer;
              }}
              onTouchEnd={() => clearTimeout((window as any).__lpTimer)}
              className={`rounded-xl p-1.5 text-center select-none transition-all cursor-pointer
                ${isSelected
                  ? 'bg-primary border-2 border-primary shadow-lg shadow-primary/30 scale-105'
                  : isDragOver
                  ? 'bg-primary/20 border-2 border-primary/50 scale-102'
                  : meal
                  ? 'bg-primary/15 border border-primary/20 hover:bg-primary/25 active:scale-95'
                  : selected !== null
                  ? 'bg-muted/50 border border-dashed border-primary/40 hover:bg-primary/10'
                  : 'bg-muted/30'
                }`}
            >
              <div className={`text-[9px] font-bold uppercase mb-1 ${isSelected ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                {day}
              </div>
              <div className={`text-[9px] leading-tight line-clamp-2 min-h-[2rem] flex flex-col items-center justify-center gap-0.5 ${isSelected ? 'text-primary-foreground' : 'text-foreground'}`}>
                {meal
                  ? <>
                      <span className="font-medium">{meal.name}</span>
                      {rating > 0 && !isSelected && <StarsDisplay rating={rating} />}
                    </>
                  : <span className={`text-[9px] ${isSelected ? 'text-primary-foreground/60' : 'text-muted-foreground/40'}`}>—</span>
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function WeeklyCalendarPage() {
  const { savedTrips, deleteTrip, cartMeals, mealPlan, selectedStore, saveTrip, swapCartMeals } = useAppStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [savedMsg, setSavedMsg] = useState(false);

  // Build sections from current cart for "save this week"
  const currentSections = useMemo(() => {
    if (!cartMeals.length) return [];
    const SECTION_ORDER = ['🥩 Meat & Poultry','🐟 Fish & Seafood','🥦 Produce','🧀 Dairy & Eggs','🌾 Dry Goods & Grains','🥫 Canned & Pantry','🍯 Condiments & Oils','🧂 Spices & Herbs','🛒 Other'];
    const sectionFor = (name: string): string => {
      const n = name.toLowerCase();
      if (['chicken','beef','pork','lamb','turkey','sausage','steak','rib','roast','loin'].some(k=>n.includes(k))) return '🥩 Meat & Poultry';
      if (['salmon','shrimp','fish','cod','tuna','halibut'].some(k=>n.includes(k))) return '🐟 Fish & Seafood';
      if (['broccoli','carrot','onion','potato','spinach','cucumber','zucchini','mushroom','tomato','apple','lemon','lime','orange'].some(k=>n.includes(k))) return '🥦 Produce';
      if (['milk','cheese','butter','cream','yogurt','egg'].some(k=>n.includes(k))) return '🧀 Dairy & Eggs';
      if (['pasta','rice','noodle','flour','bread'].some(k=>n.includes(k))) return '🌾 Dry Goods & Grains';
      if (['broth','stock','canned','bean'].some(k=>n.includes(k))) return '🥫 Canned & Pantry';
      if (['oil','vinegar','mustard','honey','soy sauce'].some(k=>n.includes(k))) return '🍯 Condiments & Oils';
      if (['garlic powder','paprika','cumin','oregano','thyme','salt','pepper','spice'].some(k=>n.includes(k))) return '🧂 Spices & Herbs';
      return '🛒 Other';
    };
    const sp = (v: unknown) => { if (typeof v === 'number') return Math.max(0,v); const p=parseFloat(String(v??'').replace(/[$,\s]/g,'')); return isNaN(p)?0:Math.max(0,p); };
    const map = new Map<string, {name:string;qty:string;price:number}[]>();
    cartMeals.forEach(meal => {
      (meal.ingredients ?? []).forEach(ing => {
        const sec = sectionFor(ing.name ?? '');
        if (!map.has(sec)) map.set(sec, []);
        map.get(sec)!.push({ name: ing.name, qty: ing.qty, price: sp(ing.price) });
      });
    });
    return SECTION_ORDER.filter(s => map.has(s)).map(s => ({ section: s, items: map.get(s)! }));
  }, [cartMeals]);

  const currentTotal = useMemo(() =>
    cartMeals.reduce((s, m) => s + (m.ingredients?.reduce((ss, i) => {
      const p = typeof i.price === 'number' ? i.price : parseFloat(String(i.price??'')) || 0;
      return ss + Math.max(0, p);
    }, 0) ?? 0), 0),
    [cartMeals]
  );

  const saveCurrentWeek = () => {
    if (!cartMeals.length) return;
    const storeName = selectedStore?.name || mealPlan?.store_name || 'My Store';
    const date = new Date().toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
    const trip: SavedTrip = {
      id:        crypto.randomUUID(),
      name:      `${storeName} — Week of ${date}`,
      store:     storeName,
      savedAt:   new Date().toISOString(),
      totalCost: currentTotal,
      meals:     cartMeals.map(m => m.name),
      sections:  currentSections,
    };
    saveTrip(trip);
    // Sync to Supabase if logged in
    if (user) {
      supabase.from('user_saved_trips').upsert({
        id:         trip.id,
        user_id:    user.id,
        name:       trip.name,
        store_name: trip.store,
        meals:      { names: trip.meals, sections: trip.sections },
        total_cost: trip.totalCost,
        created_at: trip.savedAt,
      }, { onConflict: 'id' })
      .then(({ error }) => { if (error) console.error('[FF] save week:', error.message); });
    }
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 3000);
  };

  // All meals from current plan (for archived trip navigation)
  const allCurrentMeals: Meal[] = [
    ...(mealPlan?.meals ?? []),
    ...cartMeals,
  ];

  return (
    <div className="min-h-screen bg-background pt-12 pb-24">
      <header className="sticky top-12 z-10 bg-background/95 backdrop-blur border-b border-border/50">
        <div className="container mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="font-bold text-foreground flex-1">Weekly Plans</span>
          {cartMeals.length > 0 && (
            <button onClick={saveCurrentWeek}
              className="flex items-center gap-1.5 text-xs font-bold bg-primary text-primary-foreground px-3 py-2 rounded-full hover:opacity-90 transition-opacity">
              <BookmarkPlus className="w-3.5 h-3.5" />
              {savedMsg ? 'Saved! ✓' : `Save This Week ($${currentTotal.toFixed(2)})`}
            </button>
          )}
        </div>
      </header>

      <div className="container mx-auto max-w-xl px-4 py-5">
        {/* Current week interactive grid */}
        {cartMeals.length > 0 && (
          <motion.div
            {...{initial:{opacity:0,y:12},animate:{opacity:1,y:0},transition:springPresets.gentle}}
            className="bg-primary/5 border border-primary/20 rounded-2xl p-4 mb-5"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-foreground">📅 Current Week</span>
              <span className="text-xs font-semibold text-primary">${currentTotal.toFixed(2)}</span>
            </div>

            <CurrentWeekGrid
              cartMeals={cartMeals}
              swapCartMeals={swapCartMeals}
              totalCost={currentTotal}
            />

            <p className="text-xs text-muted-foreground mt-3">
              {cartMeals.length} meal{cartMeals.length !== 1 ? 's' : ''} selected · Hit{' '}
              <strong className="text-primary">Save This Week</strong> to archive it
            </p>
          </motion.div>
        )}

        {savedTrips.length === 0 ? (
          <div className="text-center py-16">
            <CalendarDays className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-base font-semibold text-foreground mb-1">No saved weeks yet</p>
            <p className="text-sm text-muted-foreground mb-5">
              Add meals to your list and hit <strong>Save This Week</strong> to archive a full week's plan here.
            </p>
            <button onClick={() => navigate(ROUTE_PATHS.HOME)}
              className="text-primary font-semibold text-sm hover:underline">
              ← Build a meal plan
            </button>
          </div>
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="space-y-4">
            {savedTrips.map(trip => (
              <WeekCalendar
                key={trip.id}
                trip={trip}
                onDelete={() => deleteTrip(trip.id)}
                currentMeals={allCurrentMeals}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
